/**
 * IntroScreen.ts
 * Full-screen story introduction shown after the player clicks PLAY.
 *
 * Layout:
 *   • Solid black overlay covers the map (the game world is already running below)
 *   • Title lockup at top
 *   • Story paragraphs, centred
 *   • "Go" button — fades the overlay out to reveal the map, then fires onDone
 *
 * The fade is driven by PixiJS Ticker.shared so no external tick is needed.
 */

import { Container, Graphics, Text, TextStyle, Ticker } from 'pixi.js';

// ── Text styles ────────────────────────────────────────────────────────────────
const TITLE_STYLE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   13,
  fill:       '#00ffcc',
  letterSpacing: 6,
  align:      'center',
});

const BODY_STYLE = new TextStyle({
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontSize:   15,
  fill:       '#c8d8d4',
  align:      'center',
  lineHeight: 26,
  wordWrap:   true,
});

const HIGHLIGHT_STYLE = new TextStyle({
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontSize:   15,
  fontStyle:  'italic',
  fill:       '#ffffff',
  align:      'center',
  lineHeight: 26,
  wordWrap:   true,
});

const CALL_STYLE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   13,
  fill:       '#7ab8aa',
  letterSpacing: 2,
  align:      'center',
  lineHeight: 22,
  wordWrap:   true,
});

const BTN_LABEL_STYLE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   14,
  fill:       '#00ffcc',
  letterSpacing: 6,
});

// ── Story content ──────────────────────────────────────────────────────────────
const PARAGRAPHS: Array<{ text: string; style: 'body' | 'highlight' | 'call' }> = [
  {
    text:  'Songwriter Nigel has been receiving music from the future.',
    style: 'highlight',
  },
  {
    text:  'Beamed back through the Future Beacon — a device hidden at the heart of the town — these transmissions carry songs that don\'t exist yet. Songs that could change everything.',
    style: 'body',
  },
  {
    text:  'But the music industry has other ideas.',
    style: 'highlight',
  },
  {
    text:  'Streaming Moguls, corporate managers, tribute acts and a toxic music press — they\'ll do anything to silence the signal and keep the future from arriving.',
    style: 'body',
  },
  {
    text:  'Your band — Electrasy — stands between Nigel and the noise.',
    style: 'highlight',
  },
  {
    text:  'Protect the Beacon.\nClaim the venues.\nGet the music heard.',
    style: 'call',
  },
];

const MOBILE_PARAGRAPHS: Array<{ text: string; style: 'body' | 'highlight' | 'call' }> = [
  {
    text:  'Songwriter Nigel is receiving music from the future.',
    style: 'highlight',
  },
  {
    text:  'The Future Beacon is pulling songs back through time. Songs that could change everything.',
    style: 'body',
  },
  {
    text:  'The music industry wants the signal dead.',
    style: 'highlight',
  },
  {
    text:  'Streaming Moguls, managers, tribute acts and the music press are closing in.',
    style: 'body',
  },
  {
    text:  'Electrasy stands between Nigel and the noise.',
    style: 'highlight',
  },
  {
    text:  'Protect the Beacon.\nClaim the venues.\nGet the music heard.',
    style: 'call',
  },
];

const FADE_DURATION = 1.4; // seconds
const MIN_BODY_FONT = 10;

export class IntroScreen {
  private readonly container = new Container();
  private screenW = 1280;
  private screenH = 720;
  private beaconTick: ((delta: number) => void) | null = null;
  private beaconElapsed = 0;

  constructor(
    private readonly ui:     Container,
    private readonly onDone: () => void,
  ) {
    ui.addChild(this.container);
  }

  show(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    this.ui.addChild(this.container);
    this.build();
    this.container.visible = true;
    this.container.alpha   = 1;
  }

  resize(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    if (this.container.visible) this.build();
  }

  hide(): void {
    this.container.visible = false;
    this.stopBeaconTick();
  }

  private stopBeaconTick(): void {
    if (this.beaconTick) {
      Ticker.shared.remove(this.beaconTick);
      this.beaconTick = null;
    }
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private build(): void {
    this.stopBeaconTick();
    this.container.removeChildren();

    const W = this.screenW;
    const H = this.screenH;
    const isNarrow = W < 520;
    const isShort  = H < 620;
    const useMobileCopy = isNarrow || isShort;
    const marginX  = Math.max(18, Math.min(76, W * 0.1));
    const maxWidth = Math.max(240, Math.min(640, W - marginX * 2));
    const topPad   = useMobileCopy ? 12 : 28;
    const bottomPad = isShort ? 14 : 20;

    // ── Full-screen black backdrop ──────────────────────────────────────────
    const bg = new Graphics();
    bg.beginFill(0x000000, 1);
    bg.drawRect(0, 0, W, H);
    bg.endFill();
    this.container.addChild(bg);

    // ── Subtle top rule ─────────────────────────────────────────────────────
    const rule = new Graphics();
    rule.lineStyle(1, 0x00ffcc, 0.25);
    rule.moveTo(marginX, topPad + (useMobileCopy ? 34 : 32));
    rule.lineTo(W - marginX, topPad + (useMobileCopy ? 34 : 32));
    this.container.addChild(rule);

    // ── Title — "FUTURE ◉ BEACON" lockup ────────────────────────────────────
    const titleStyle = TITLE_STYLE.clone();
    const titleFS = useMobileCopy ? 11 : 13;
    titleStyle.fontSize = titleFS;
    titleStyle.letterSpacing = useMobileCopy ? 3 : 6;
    const word1 = new Text('FUTURE', titleStyle);
    const word2 = new Text('BEACON', titleStyle);

    const beaconScale = useMobileCopy ? 0.72 : 1;
    const slotW       = Math.round(titleFS * 1.1);
    const totalW      = word1.width + slotW + word2.width;
    const lockLeft    = W / 2 - totalW / 2;
    const titleMidY   = topPad + word1.height / 2;

    word1.anchor.set(0, 0);
    word1.position.set(lockLeft, topPad);
    this.container.addChild(word1);

    word2.anchor.set(0, 0);
    word2.position.set(lockLeft + word1.width + slotW, topPad);
    this.container.addChild(word2);

    // Animated beacon glyph centred in the slot between the two words
    const beaconGfx = new Graphics();
    beaconGfx.position.set(Math.round(lockLeft + word1.width + slotW / 2), Math.round(titleMidY));
    this.container.addChild(beaconGfx);

    this.beaconElapsed = 0;
    this.beaconTick = (delta: number) => {
      this.beaconElapsed += delta / 60;
      const pulse = 0.5 + 0.5 * Math.sin(this.beaconElapsed * 2.4);
      const s     = beaconScale;
      beaconGfx.clear();
      beaconGfx.beginFill(0x37e0d0, 0.10 + 0.18 * pulse);
      beaconGfx.drawCircle(0, 0, (13 + pulse * 6) * s);
      beaconGfx.endFill();
      beaconGfx.beginFill(0x6ff0e4, 0.22 + 0.20 * pulse);
      beaconGfx.drawCircle(0, 0, 7 * s);
      beaconGfx.endFill();
      beaconGfx.beginFill(0xc9fff8, 0.92);
      beaconGfx.drawCircle(0, 0, (3 + pulse * 1.2) * s);
      beaconGfx.endFill();
    };
    Ticker.shared.add(this.beaconTick);

    // ── Story paragraphs ────────────────────────────────────────────────────
    const btnW = useMobileCopy ? Math.min(144, W - marginX * 2) : 160;
    const btnH = useMobileCopy ? 40 : 44;
    const btnY = H - btnH - bottomPad;
    const contentTop = topPad + (useMobileCopy ? 54 : 72);
    const availableTextH = Math.max(140, btnY - contentTop - (useMobileCopy ? 22 : 26));

    const paragraphs = useMobileCopy ? MOBILE_PARAGRAPHS : PARAGRAPHS;
    const initialFont = useMobileCopy ? 12 : 15;
    const initialGap  = useMobileCopy ? 7 : 18;
    const layout = this.measureParagraphLayout(paragraphs, maxWidth, availableTextH, initialFont, initialGap);
    let y = contentTop + Math.max(0, (availableTextH - layout.totalH) * 0.35);

    for (const t of layout.texts) {
      t.anchor.set(0.5, 0);
      t.position.set(W / 2, y);
      this.container.addChild(t);

      y += t.height + layout.gap;
    }

    // ── Bottom rule ─────────────────────────────────────────────────────────
    const rule2 = new Graphics();
    rule2.lineStyle(1, 0x00ffcc, 0.15);
    const rule2Y = Math.min(btnY - 16, y + (isShort ? 2 : 8));
    rule2.moveTo(W * 0.3, rule2Y);
    rule2.lineTo(W * 0.7, rule2Y);
    this.container.addChild(rule2);

    // ── Go button ───────────────────────────────────────────────────────────
    const btnX  = W / 2 - btnW / 2;

    const btnBg = new Graphics();
    this.container.addChild(btnBg);

    const btnLabelStyle = BTN_LABEL_STYLE.clone();
    btnLabelStyle.fontSize = useMobileCopy ? 13 : 14;
    btnLabelStyle.letterSpacing = useMobileCopy ? 4 : 6;
    const btnLabel = new Text('GO', btnLabelStyle);
    btnLabel.anchor.set(0.5, 0.5);
    btnLabel.position.set(W / 2, btnY + btnH / 2);
    this.container.addChild(btnLabel);

    const drawBtn = (hover: boolean) => {
      btnBg.clear();
      btnBg.lineStyle(1, 0x00ffcc, hover ? 0.9 : 0.5);
      btnBg.beginFill(hover ? 0x0d2d25 : 0x050f0c, 1);
      btnBg.drawRoundedRect(btnX, btnY, btnW, btnH, 4);
      btnBg.endFill();
    };
    drawBtn(false);

    // Hit area over the button
    const hit = new Graphics();
    hit.beginFill(0xffffff, 0.001);
    hit.drawRect(btnX, btnY, btnW, btnH);
    hit.endFill();
    hit.eventMode = 'static';
    hit.cursor    = 'pointer';

    hit.on('pointerenter', () => { drawBtn(true);  btnLabel.style.fill = '#ffffff'; });
    hit.on('pointerleave', () => { drawBtn(false); btnLabel.style.fill = '#00ffcc'; });
    hit.on('pointerdown',  () => this.startFade());
    this.container.addChild(hit);

    // ── Keep whole screen interactive so nothing bleeds through ─────────────
    bg.eventMode = 'static';
  }

  private measureParagraphLayout(
    paragraphs: Array<{ text: string; style: 'body' | 'highlight' | 'call' }>,
    maxWidth: number,
    availableH: number,
    initialFont: number,
    initialGap: number,
  ): {
    texts: Text[];
    gap: number;
    totalH: number;
  } {
    let bestTexts: Text[] = [];
    let bestGap = initialGap;
    let bestTotal = Infinity;

    for (let font = initialFont; font >= MIN_BODY_FONT; font--) {
      const gap = Math.max(4, initialGap - (initialFont - font) * 2);
      const lineHeight = Math.round(font * 1.58);
      const callLineHeight = Math.round(font * 1.45);
      const texts = paragraphs.map((para) => {
        const style = para.style === 'highlight' ? HIGHLIGHT_STYLE.clone()
                    : para.style === 'call'      ? CALL_STYLE.clone()
                    : BODY_STYLE.clone();
        style.fontSize = para.style === 'call' ? Math.max(MIN_BODY_FONT, font - 1) : font;
        style.lineHeight = para.style === 'call' ? callLineHeight : lineHeight;
        style.wordWrapWidth = maxWidth;
        if (para.style === 'call') style.letterSpacing = font <= 12 ? 1 : 2;
        return new Text(para.text, style);
      });
      const total = texts.reduce((sum, text) => sum + text.height, 0) + gap * (texts.length - 1);

      if (total < bestTotal) {
        for (const text of bestTexts) text.destroy();
        bestTexts = texts;
        bestGap = gap;
        bestTotal = total;
      } else {
        for (const text of texts) text.destroy();
      }

      if (total <= availableH) break;
    }

    return { texts: bestTexts, gap: bestGap, totalH: bestTotal };
  }

  /** Fade the overlay to transparent then fire onDone. */
  private startFade(): void {
    let elapsed = 0;

    const tick = (delta: number) => {
      // delta is in PixiJS ticks (~1 per frame at 60 fps); convert to seconds
      elapsed += delta / 60;
      this.container.alpha = Math.max(0, 1 - elapsed / FADE_DURATION);

      if (elapsed >= FADE_DURATION) {
        Ticker.shared.remove(tick);
        this.stopBeaconTick();
        this.container.visible = false;
        this.onDone();
      }
    };

    Ticker.shared.add(tick);
  }
}
